import { reportError } from '../../monitoring/errorMonitor';
import React, { FC } from 'react';
import { TemplateMediaContainer } from '../TemplateMediaContainer/TemplateMediaContainer';
import { InspirationalTemplate } from '../../domain/entities/inspirationalTemplate';
import { useOnViewportEnter } from '../../hooks/useOnViewportEnter';
import { useMediaLoading } from '../../hooks/useMediaLoading';

export type TemplateVideoProps = {
  className?: string;
  template: InspirationalTemplate;
  index?: number;
  categorySlug?: string;
};

export const TemplateVideo: FC<TemplateVideoProps> = ({
  template,
  className,
  index,
  categorySlug,
}) => {
  const { onMediaLoadStart, onMediaLoaded, isMediaLoading } = useMediaLoading();

  const videoRef = useOnViewportEnter<HTMLVideoElement>(
    (ref) => {
      ref.current?.play().catch((error) => {
        // AbortError is expected when media is removed from DOM (e.g. on unmount/scroll)
        if (error?.name !== 'AbortError') {
          reportError(error);
        }
      });
    },
    {
      threshold: 0,
    },
  );

  return (
    <TemplateMediaContainer
      template={template}
      categorySlug={categorySlug}
      index={index}
      isLoading={isMediaLoading}
      onMouseEnter={() => videoRef.current?.pause()}
      onMouseLeave={() =>
        videoRef.current?.play().catch((error) => {
          // AbortError is expected when media is removed from DOM (e.g. on unmount/scroll)
          if (error?.name !== 'AbortError') {
            reportError(error);
          }
        })
      }
    >
      <video
        src={template.thumbnailUrl}
        className={className}
        preload="none"
        ref={videoRef}
        muted
        loop
        playsInline
        onLoadedData={onMediaLoaded}
        onLoadStart={onMediaLoadStart}
        onError={(event) => {
          const target = event.target as HTMLVideoElement;
          const mediaError = target?.error;
          const attemptedUrl = target?.currentSrc || template.thumbnailUrl;
          const errorDetails = {
            url: attemptedUrl,
            templateId: template.templateId,
            errorCode: mediaError?.code,
            errorMessage: mediaError?.message,
            component: 'TemplateVideo',
          };
          console.error('[TemplateVideo] Video load failed:', errorDetails);
          reportError(
            new Error(errorDetails.errorMessage || 'Video load failed'),
            { errorDetails },
          );
        }}
      />
    </TemplateMediaContainer>
  );
};
